<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Work</div>
                <div class="page-bar-main">Board Templates</div>
            </div>
            <div style="display:flex;align-items:center">
                <button type="button" class="btn-light" @click="$router.push('/decks')">Back</button>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="form-wrapper template-page-wrapper">
                <div style="margin-bottom:20px">
                    Templates show up when someone adds a board. Picking one starts the board with these columns and tags, which can still be changed on the board afterwards.
                    The default template is what the Default option and new task managers start with. With no default set, they get To Do, In Progress and Done.
                </div>

                <div v-if="!canManage" class="template-empty">Only admins can manage board templates.</div>
                <template v-else>
                    <div v-if="!templates.length && !loading" class="template-empty">No templates yet. Add one below.</div>

                    <div v-for="(t, i) in templates" :key="t.ID || `new-${i}`" class="template-item">
                        <div class="template-item-head" @click="toggleOpen(i)">
                            <div class="template-item-summary">
                                <span class="template-item-name">{{ t.Name || '(untitled template)' }}<span v-if="t.IsDefault" class="template-default-pill">Default</span></span>
                                <span class="template-item-cols">
                                    <span v-for="(col, ci) in t.Columns" :key="ci" class="template-col-chip" :style="'background:' + col.color">{{ col.name }}</span>
                                </span>
                                <span v-if="t.Tags.length" class="template-item-cols">
                                    <span v-for="(tag, ti) in t.Tags" :key="ti" class="template-tag-chip" :style="'background:' + tag.color">{{ tag.name }}</span>
                                </span>
                            </div>
                            <div class="template-item-head-right">
                                <span v-if="t.ID && !t.IsDefault" class="template-make-default" @click.stop="setDefault(t)">Make Default</span>
                                <svg @click.stop="onDeleteTemplateClick(t)" class="template-delete-icon" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6 16.538l-4.592-4.548 4.546-4.587-1.416-1.403-4.545 4.589-4.588-4.543-1.405 1.405 4.593 4.552-4.547 4.592 1.405 1.405 4.555-4.596 4.591 4.55 1.403-1.416z"/></svg>
                                <span class="template-chevron" :class="openIndex == i ? 'open' : ''">&#9656;</span>
                            </div>
                        </div>

                        <div v-if="openIndex == i" class="template-item-body">
                            <h3 class="template-label" style="margin-top:0">Template name</h3>
                            <input type="text" v-model="t.Name" placeholder="Template name" />

                            <h3 class="template-label">Description</h3>
                            <input type="text" v-model="t.Description" placeholder="Shown under the name when adding a board" />

                            <h3 class="template-label">Columns <span class="template-hint">(drag to reorder)</span></h3>
                            <draggable v-model="t.Columns" handle=".template-col-handle">
                                <div v-for="(col, ci) in t.Columns" :key="ci" class="template-col-row">
                                    <span class="template-col-handle" title="Drag to reorder">&#8942;&#8942;</span>
                                    <input type="text" v-model="col.name" placeholder="Column name" />
                                    <div class="crm-board-color-select">
                                        <select v-model="col.color">
                                            <option v-for="(color, k) in $store.state.colorPalette" :key="k" :value="color.hex">{{ color.label }}</option>
                                        </select>
                                        <input type="color" v-model="col.color" title="Custom color" />
                                    </div>
                                    <svg-icon icon="x" class="template-remove" @click="t.Columns.splice(ci, 1)" />
                                </div>
                            </draggable>
                            <div class="template-col-links">
                                <span class="template-addlink" @click="t.Columns.push({ name: '', color: NEW_COLUMN_COLOR })">+ Add Column</span>
                                <!-- Same as Edit Columns on a board: from the last column (Done) backwards -->
                                <span v-if="t.Columns.length" class="template-addlink" @click="applyDefaultColumnColors(t.Columns, $store.state.colorPalette)">Apply Default Colors</span>
                            </div>

                            <h3 class="template-label">Tags <span class="template-hint">(optional, drag to reorder)</span></h3>
                            <draggable v-model="t.Tags" handle=".template-col-handle">
                                <div v-for="(tag, ti) in t.Tags" :key="ti" class="template-col-row">
                                    <span class="template-col-handle" title="Drag to reorder">&#8942;&#8942;</span>
                                    <input type="text" v-model="tag.name" placeholder="Tag name" />
                                    <div class="crm-board-color-select">
                                        <select v-model="tag.color">
                                            <option v-for="(color, k) in $store.state.colorPalette" :key="k" :value="color.hex">{{ color.label }}</option>
                                        </select>
                                        <input type="color" v-model="tag.color" title="Custom color" />
                                    </div>
                                    <svg-icon icon="x" class="template-remove" @click="t.Tags.splice(ti, 1)" />
                                </div>
                            </draggable>
                            <div class="template-addlink" @click="t.Tags.push({ name: '', color: '#000000' })">+ Add Tag</div>

                            <input :class="t._saving ? 'disable-button' : ''" type="submit" value="Save Template" @click="saveTemplate(t)" />
                        </div>
                    </div>

                    <div style="display:flex">
                        <div class="add-btn" @click="addTemplate()">+ Add Template</div>
                    </div>
                </template>
            </div>
        </div>

        <confirm-delete v-if="templatePendingDelete" @confirm="deleteTemplateConfirmed()" @cancel="() => { templatePendingDelete = null; }" :msg="deleteTemplateConfirmMsg"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next';
import SvgIcon from '@/components/SvgIcon.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import { NEW_COLUMN_COLOR, applyDefaultColumnColors } from '@/columnColors';

// Admin-managed starter column sets for new company boards (companyboardtemplates.ts)
export default defineComponent({
    name: 'BoardTemplates',
    components: {
        draggable: VueDraggableNext,
        SvgIcon,
        ConfirmDelete
    },
    data() {
        return {
            NEW_COLUMN_COLOR,
            templates: [] as any,
            loading: true,
            openIndex: null as number | null, // only one template's editor is expanded at a time
            templatePendingDelete: null as any
        }
    },
    computed: {
        canManage(): boolean {
            return this.$store.state.user.role == 'Admin' || !!this.$store.state.user.superAdmin;
        },
        deleteTemplateConfirmMsg(): string {
            const t = this.templatePendingDelete;
            if(!t) {
                return '';
            }
            const msg = `Delete the "${t.Name}" template? Boards already made from it keep their columns.`;
            return t.IsDefault ? msg + ' It\'s the default, so new boards and task managers will go back to To Do, In Progress and Done.' : msg;
        }
    },
    async created() {
        if(this.canManage) {
            await this.getTemplates();
        }
        this.loading = false;
        this.$store.state.routeLoaded = true;
    },
    methods: {
        applyDefaultColumnColors,
        async getTemplates() {
            await axios.get(`${this.$store.state.apiUrl}/api/companyboardtemplates`)
                .then(resp => {
                    this.templates = resp.data.map((t: any) => ({
                        ...t,
                        Columns: t.ColumnsJSON ? JSON.parse(t.ColumnsJSON) : [],
                        Tags: t.TagsJSON ? JSON.parse(t.TagsJSON) : [],
                        _saving: false
                    }));
                })
                .catch(e => {
                    console.log(e);
                })
        },
        toggleOpen(i: number) {
            this.openIndex = this.openIndex === i ? null : i;
        },
        addTemplate() {
            this.templates.push({
                ID: null,
                Name: '',
                Description: '',
                Columns: [ { name: 'To Do', color: '#D4D4D4' }, { name: 'In Progress', color: '#D4D4D4' }, { name: 'Done', color: '#D4D4D4' } ],
                Tags: [],
                _saving: false
            });
            this.openIndex = this.templates.length - 1;
        },
        async saveTemplate(t: any) {
            if(!t.Name || !String(t.Name).trim()) {
                alert('Include a name for this template');
                return;
            }
            const names = t.Columns.map((c: any) => String(c.name || '').trim().toLowerCase()).filter((n: string) => n);
            if(!names.length) {
                alert('Add at least one column');
                return;
            }
            if(new Set(names).size != names.length) {
                alert('Two columns have the same name. Column names need to be different.');
                return;
            }
            const tagNames = t.Tags.map((tag: any) => String(tag.name || '').trim().toLowerCase()).filter((n: string) => n);
            if(new Set(tagNames).size != tagNames.length) {
                alert('Two tags have the same name. Tag names need to be different.');
                return;
            }

            t._saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/companyboardtemplates`, {
                ID: t.ID,
                Name: t.Name,
                Description: t.Description,
                Columns: t.Columns,
                Tags: t.Tags
            })
                .then(resp => {
                    t.ID = resp.data.ID;
                    t.Columns = JSON.parse(resp.data.ColumnsJSON);
                    t.Tags = JSON.parse(resp.data.TagsJSON || '[]');
                    this.openIndex = null;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error saving this template');
                })
            t._saving = false;
        },
        async setDefault(t: any) {
            await axios.post(`${this.$store.state.apiUrl}/api/companyboardtemplates/default`, { ID: t.ID })
                .then(() => {
                    this.templates.forEach((other: any) => { other.IsDefault = other === t; });
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error setting the default template');
                })
        },
        onDeleteTemplateClick(t: any) {
            if(!t.ID) {
                // Never saved: just drop it, no confirmation needed
                this.removeTemplateLocally(t);
                return;
            }
            this.templatePendingDelete = t;
        },
        removeTemplateLocally(t: any) {
            const i = this.templates.indexOf(t);
            if(i === -1) {
                return;
            }
            this.templates.splice(i, 1);
            if(this.openIndex === i) {
                this.openIndex = null;
            } else if(this.openIndex !== null && this.openIndex > i) {
                this.openIndex -= 1;
            }
        },
        async deleteTemplateConfirmed() {
            const t = this.templatePendingDelete;
            this.templatePendingDelete = null;
            if(!t) {
                return;
            }
            await axios.delete(`${this.$store.state.apiUrl}/api/companyboardtemplates/${t.ID}`)
                .then(() => {
                    this.removeTemplateLocally(t);
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error deleting this template');
                })
        }
    }
});
</script>

<style scoped>
.template-page-wrapper { max-width: 700px; width: 700px; }
.template-empty { color: #a6a29d; font-size: 14px; margin-bottom: 20px; }
.template-item { border: 1px solid var(--mid-gray); border-radius: 10px; padding: 15px 20px; margin-bottom: 15px; }

.template-item-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; cursor: pointer; }
.template-item-summary { min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.template-item-name { font-weight: 700; font-size: 15px; }
.template-item-cols { display: flex; flex-wrap: wrap; gap: 4px; }
.template-col-chip { font-size: 11px; font-weight: 600; color: #2c2a29; border-radius: 4px; padding: 1px 7px; border: 1px solid rgba(0,0,0,.12); }
.template-tag-chip { font-size: 11px; font-weight: 700; color: white; border-radius: 3px; padding: 1px 6px; }
.template-item-head-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
.template-default-pill { margin-left: 8px; font-size: 11px; font-weight: 700; color: var(--primary-color); border: 1px solid var(--primary-color); border-radius: 10px; padding: 1px 8px; vertical-align: middle; }
.template-make-default { font-size: 13px; color: #646464; text-decoration: underline; cursor: pointer; white-space: nowrap; }
.template-make-default:hover { color: var(--primary-color); }
.template-delete-icon { color: red; width: 18px; height: 18px; cursor: pointer; flex-shrink: 0; }
.template-chevron { display: inline-block; color: #a6a29d; transition: transform .15s; }
.template-chevron.open { transform: rotate(90deg); }

.template-item-body { margin-top: 18px; }
.template-label { margin-top: 20px; margin-bottom: 8px; font-size: 14px; }
.template-hint { font-weight: normal; color: #a6a29d; font-size: 12px; }

.template-col-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.template-col-row input[type=text] { flex: 1; margin-bottom: 0; }
.template-col-handle { cursor: move; color: #a6a29d; letter-spacing: -3px; font-size: 13px; padding: 0 4px 0 0; user-select: none; }
.template-remove { width: 14px !important; height: 14px !important; cursor: pointer; color: #a6a29d; flex-shrink: 0; }
.template-addlink { color: #646464; font-weight: bold; font-size: 13px; cursor: pointer; margin: 4px 0 15px; }
.template-col-links { display: flex; flex-wrap: wrap; gap: 18px; margin: 4px 0 15px; }
.template-col-links .template-addlink { margin: 0; }
.template-addlink:hover { color: var(--primary-color); }
</style>
